import { HttpParams } from '@angular/common/http';
import { CompanyBatchEntity, GeoCountryEntity, TransientFileMetaEntity } from '@nk/data-entities-lib';
import { arrayUnique, Dict, dictToKeys, flatten1, includes, includesAny } from '@nk/utils-lib';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  ACCOUNTS_FIELD_TYPES,
  CUSTOM_STYLE_COLUMNS,
  DEFAULT_REQUIRED_COLUMNS,
  FIELD_GROUP_TO_TRANSLATION_OVERRIDES,
  FIELD_TYPES,
  FILTERABLE_COLUMNS,
  SELECTABLE_COLUMNS,
  SORTABLE_COLUMNS,
} from './company-browser-config';
import {
  BrowserFilterChange,
  CompanyBrowserColumn,
  CompanyBrowserExtendedCompositeCollection,
  CompanyBrowserFilter,
  CompanyBrowserOrdering,
  FilterChangeType,
  OPERATORS,
  TabularFileFormat,
  toAbbr,
} from './company-browser-entities';
import { CompanyBrowserService } from './company-browser.service';

// TODO: Suggestion: Rename to CompanyBrowserController and company-browser-controller.ts
export class CompanyBrowserController {
  public allColumns: CompanyBrowserColumn[] = [];

  public limit = 100;
  public offset = 0;

  public companyCount: number | null = null;

  public selectedColumns: CompanyBrowserColumn[] = [];
  private _selectedColumnsSubject = new ReplaySubject<CompanyBrowserColumn[]>(1);
  public selectedColumns$ = this._selectedColumnsSubject.asObservable();

  private _filtersSubject = new ReplaySubject<CompanyBrowserFilter[]>(1);
  public filters$ = this._filtersSubject.asObservable();

  private _filterChange = new ReplaySubject<BrowserFilterChange>(1);
  public filterChange$ = this._filterChange.asObservable();

  private _onExecutionStartSubject = new ReplaySubject<{ params: HttpParams }>(1); // here new parameters will be pushed
  public onExecutionStart$ = this._onExecutionStartSubject.asObservable();

  private _browserResultsSubject = new ReplaySubject<CompanyBrowserExtendedCompositeCollection>(1);
  public browserResults$ = this._browserResultsSubject.asObservable();

  private _executeSubscription: Subscription;
  public prevResult: CompanyBrowserExtendedCompositeCollection;

  // Here you can listen to data coming in.

  public filterTypeChange(
    filterType: FilterChangeType,
    fieldKey: string,
    operator?: string,
  ): Observable<CompanyBrowserFilter> {
    return this.filterChange$.pipe(
      filter((flt) => {
        return flt.filterChangeType === filterType;
      }),
      filter((flt) => {
        if (!flt.browserFilter) {
          return false;
        }
        if (operator) {
          return operator === flt.browserFilter.operator && fieldKey === flt.browserFilter.fieldKey;
        } else {
          return fieldKey === flt.browserFilter.fieldKey;
        }
      }),
      map((flt) => flt.browserFilter),
    );
  }

  private _orderingsSubject = new ReplaySubject<CompanyBrowserOrdering[]>(1);
  public orderings$ = this._orderingsSubject.asObservable();

  private _loadingSubject = new ReplaySubject<boolean>(1);
  private _loadingCountSubject = new ReplaySubject<boolean>(1);

  public loading$ = this._loadingSubject.asObservable();
  public loadingCount$ = this._loadingCountSubject.asObservable();

  private readonly _appCustomerUuid: string;

  private _filterAccountingYear: number;
  private _filterAccountingYearSubject = new ReplaySubject<number>(1);
  public filterAccountingYear$ = this._filterAccountingYearSubject.asObservable();

  private _fromAccountingYear: number;
  private _toAccountingYear: number;
  private _descAccountingYearsFlag: boolean;

  constructor(
    appCustomerUuid: string,
    public membershipGeoCountryFocus: GeoCountryEntity[],
    public languageCode: string,
    public fileType: TabularFileFormat,
    private _companyBrowserService: CompanyBrowserService,
    private _companyFlagTypeUuidToAppFeatureKeyDict: Dict<string>,
    selectedColumnFieldKeys: string[],
    public filters: CompanyBrowserFilter[] = [],
    public orderings: CompanyBrowserOrdering[] = [],
    public unused = true,
    filterAccountingYear?: number,
    fromAccountingYear?: number,
    toAccountingYear?: number,
    descAccountingYearsFlag?: boolean,
  ) {
    selectedColumnFieldKeys = selectedColumnFieldKeys.filter((fieldKey) => fieldKey !== 'company.name');
    selectedColumnFieldKeys = ['company.name', ...selectedColumnFieldKeys];
    this._appCustomerUuid = appCustomerUuid;
    const fieldGroups = arrayUnique([
      ...arrayUnique(dictToKeys(SELECTABLE_COLUMNS).map((key) => key.split('.')[0])),
      ...arrayUnique(dictToKeys(FILTERABLE_COLUMNS).map((key) => key.split('.')[0])),
      ...arrayUnique(dictToKeys(SORTABLE_COLUMNS).map((key) => key.split('.')[0])),
    ]);

    const unhandledFieldKeys = [
      ...dictToKeys(SELECTABLE_COLUMNS),
      ...dictToKeys(FILTERABLE_COLUMNS),
      ...dictToKeys(SORTABLE_COLUMNS),
    ];

    let allFieldKeys: string[] = [];

    for (const fieldGroup of fieldGroups) {
      allFieldKeys = [
        ...allFieldKeys,
        ...arrayUnique(unhandledFieldKeys.filter((key) => key.startsWith(fieldGroup + '.'))),
      ];
    }

    for (const fieldKey of allFieldKeys) {
      const [fieldGroup, fieldName] = fieldKey.split('.');
      let translationKey: string;
      if (FIELD_GROUP_TO_TRANSLATION_OVERRIDES[fieldGroup]) {
        translationKey = FIELD_GROUP_TO_TRANSLATION_OVERRIDES[fieldGroup] + '.' + fieldName;
      } else {
        translationKey = 'company_browser_headings.' + fieldKey;
      }
      const column = new CompanyBrowserColumn(
        fieldKey,
        SELECTABLE_COLUMNS[fieldKey] === undefined ? SELECTABLE_COLUMNS[fieldGroup] : SELECTABLE_COLUMNS[fieldKey],
        SELECTABLE_COLUMNS[fieldKey] === undefined ? !!SELECTABLE_COLUMNS[fieldGroup] : !!SELECTABLE_COLUMNS[fieldKey],
        FILTERABLE_COLUMNS[fieldKey] === undefined ? !!FILTERABLE_COLUMNS[fieldGroup] : !!FILTERABLE_COLUMNS[fieldKey],
        includes(CUSTOM_STYLE_COLUMNS, fieldKey), // TODO: Consider moving to where styles are rendered.
        translationKey,
        SORTABLE_COLUMNS[fieldKey] === undefined ? !!SORTABLE_COLUMNS[fieldGroup] : !!SORTABLE_COLUMNS[fieldKey],
        DEFAULT_REQUIRED_COLUMNS[fieldKey] === undefined
          ? !!DEFAULT_REQUIRED_COLUMNS[fieldGroup]
          : !!DEFAULT_REQUIRED_COLUMNS[fieldKey],
        FIELD_TYPES[fieldKey] === undefined ? FIELD_TYPES[fieldGroup] : FIELD_TYPES[fieldKey],
        ACCOUNTS_FIELD_TYPES[fieldKey] === undefined
          ? ACCOUNTS_FIELD_TYPES[fieldGroup]
          : ACCOUNTS_FIELD_TYPES[fieldKey],
      );
      this.allColumns.push(column);
    }
    this.selectedColumns = this.allColumns
      .filter((col) => col.selectable)
      .filter((col) => includes(selectedColumnFieldKeys, col.fieldKey));

    this._selectedColumnsSubject.next(this.selectedColumns);
    this._filtersSubject.next(filters);
    this._orderingsSubject.next(orderings);
    this._loadingSubject.next(false);

    this.filterAccountingYear = filterAccountingYear;
    this.fromAccountingYear = fromAccountingYear;
    this.toAccountingYear = toAccountingYear;
    this.descAccountingYearsFlag = descAccountingYearsFlag;

    this._executeSubscription = this.onExecutionStart$
      .pipe(
        debounceTime(300),
        tap(() => {
          this._loadingSubject.next(true);
          this._loadingCountSubject.next(true);
        }),
        switchMap(({ params }) => {
          return combineLatest([
            this._companyBrowserService.fetchExtendedCompanyBrowserResult(params),
            this._companyBrowserService.fetchExtendedCompanyBrowserCount(params),
          ]);
        }),
      )
      .subscribe({
        next: ([result, count]) => {
          this._loadingSubject.next(false);
          if (result) {
            this.prevResult = result;
            this._browserResultsSubject.next(result); // <- Passes data to results.
          }

          if (typeof count === 'number') {
            if (result) {
              result.count = count;
              this.companyCount = result.count;
            } else {
              this.companyCount = count;
            }
            this._loadingCountSubject.next(false);
          }
        },
        error: (error: unknown) => {
          this._loadingSubject.next(false);
          this._loadingCountSubject.next(false);
          this._browserResultsSubject.error(error);
        },
      });
  }

  public setOrderings(orderings: CompanyBrowserOrdering[]): void {
    this.orderings = orderings;
    this._orderingsSubject.next(orderings);
  }

  public activateColumns(activatingFieldKeys: string[]): void {
    const existingFieldKeys = this.selectedColumns.map((col) => col.fieldKey);
    this.selectedColumns = this.allColumns
      .filter((col) => col.selectable)
      .filter((col) => includes(activatingFieldKeys, col.fieldKey) || includes(existingFieldKeys, col.fieldKey));
    this._selectedColumnsSubject.next(this.selectedColumns);
  }

  public deactivateColumns(deactivatingFieldKeys: string[]): void {
    this.selectedColumns = this.selectedColumns.filter((col) => !includes(deactivatingFieldKeys, col.fieldKey));
    this._selectedColumnsSubject.next(this.selectedColumns);
  }

  public buildHttpParams(limit?: number): HttpParams {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('app_customer_uuid', this._appCustomerUuid);

    if (limit != null) {
      httpParams = httpParams.append('limit', limit.toString());
    } else if (this.limit) {
      httpParams = httpParams.append('limit', this.limit.toString());
    }

    if (this.offset) {
      httpParams = httpParams.append('offset', this.offset.toString());
    }
    if (this.fileType) {
      httpParams = httpParams.append('file_type', this.fileType);
    }
    if (this.languageCode) {
      httpParams = httpParams.append('language_code', this.languageCode);
    }

    for (const f of this.filters) {
      httpParams = httpParams.append(toAbbr(f.fieldKey), `${f.operator}:${f.values.join(',')}`);
    }

    const selectedEntitiesString = this.selectedColumns.map((col) => toAbbr(col.fieldKey)).join(',');
    httpParams = httpParams.append('selected_entities', selectedEntitiesString);

    const orderByString = this.orderings
      .map((sortValue) => {
        return toAbbr(sortValue.fieldKey) + ':' + sortValue.orderDirection;
      })
      .join(',');
    if (orderByString.length > 0) {
      httpParams = httpParams.append('order_by', orderByString);
    }

    if (this.filterAccountingYear) {
      httpParams = httpParams.append('filter_accounting_year', this.filterAccountingYear.toString());
    }
    if (this.fromAccountingYear) {
      httpParams = httpParams.append('from_accounting_year', this.fromAccountingYear.toString());
    }
    if (this.toAccountingYear) {
      httpParams = httpParams.append('to_accounting_year', this.toAccountingYear.toString());
    }
    if (this.descAccountingYearsFlag) {
      httpParams = httpParams.append('desc_accounting_years_flag', (this.descAccountingYearsFlag ? 1 : 0).toString());
    }

    return httpParams;
  }

  public addFilter(flt: CompanyBrowserFilter): void {
    this.removeFilter(flt.fieldKey, flt.operator, true);
    this.filters = [...this.filters, flt];
    this._filtersSubject.next(this.filters);
    this._filterChange.next({ browserFilter: flt, filterChangeType: FilterChangeType.Added });
  }

  public removeFilter(fieldKey: string, operator?: string, skipChangeUpdate = false): void {
    let filterRemoved = [];
    if (includes(fieldKey, '.')) {
      filterRemoved = this.filters.filter(
        (flt) => flt.fieldKey === fieldKey && (!operator || operator === flt.operator),
      );
      this.filters = this.filters.filter(
        (flt) => !(flt.fieldKey === fieldKey && (!operator || operator === flt.operator)),
      );
    } else {
      this.filters = this.filters.filter(
        (flt) => !(flt.fieldGroup === fieldKey && (!operator || operator === flt.operator)),
      );
      filterRemoved = this.filters.filter(
        (flt) => flt.fieldGroup === fieldKey && (!operator || operator === flt.operator),
      );
    }

    if (filterRemoved.length > 0) {
      filterRemoved.forEach((flt) => {
        flt.values = [];
        flt.value = null;
        if (!skipChangeUpdate) {
          this._filterChange.next({ browserFilter: flt, filterChangeType: FilterChangeType.Remove });
        }
      });
    } else {
      if (!skipChangeUpdate) {
        this._filterChange.next({ browserFilter: filterRemoved[0], filterChangeType: FilterChangeType.Remove });
      }
    }
    this._filtersSubject.next(this.filters);
  }

  public removeFilterValue(fieldKey: string, operator: string, value: {}): void {
    const flt = this.findFilter(fieldKey, operator);
    const nextValues = flt.values.filter((v) => v !== value);
    if (nextValues.length === 0) {
      this.removeFilter(fieldKey, operator);
    } else {
      flt.values = nextValues;
      this._filtersSubject.next(this.filters);
      this._filterChange.next({ browserFilter: flt, filterChangeType: FilterChangeType.Remove });
    }
  }

  public removeAllFilters(): void {
    const filtersBeforeRemoval = this.filters;
    this.filters = [];

    // Force country filter based on geo focus.
    const orgNrSchema = this.membershipGeoCountryFocus.map((a) => a.country_code_two);
    const orgNrSchemaFilter = new CompanyBrowserFilter('company.org_nr_schema', OPERATORS.In, orgNrSchema);
    this.filters = [...this.filters, orgNrSchemaFilter];

    filtersBeforeRemoval.forEach((flt) => {
      this._filterChange.next({ browserFilter: flt, filterChangeType: FilterChangeType.Remove });
    });
    this._filtersSubject.next(this.filters);
  }

  public findFilters(fieldKey: string, operator?: string): CompanyBrowserFilter[] {
    if (includes(fieldKey, '.')) {
      return this.filters.filter((f) => f.fieldKey === fieldKey && (!operator || operator === f.operator));
    } else {
      return this.filters.filter((f) => f.fieldGroup === fieldKey && (!operator || operator === f.operator));
    }
  }

  public findFilter(fieldKey: string, operator: string): CompanyBrowserFilter | null {
    const filters = this.findFilters(fieldKey, operator);
    return filters && filters.length > 0 ? filters[0] : null;
  }

  public createBatch(): Observable<CompanyBatchEntity> {
    this.unused = false;
    const params = this.buildHttpParams();
    return this._companyBrowserService.postCompanyBatchFromCompanyBrowserResult(params, this._appCustomerUuid);
  }

  public createExcel(limit: number): Observable<TransientFileMetaEntity> {
    this.unused = false;
    const params = this.buildHttpParams(limit);
    return this._companyBrowserService.postCompanyBrowserTransientFile(params);
  }

  public execute(): void {
    this.unused = false;
    const params = this.buildHttpParams();
    if (params.toString().length >= 2000) {
      this._onExecutionStartSubject.error('too_long_url_error');
    } else {
      this._onExecutionStartSubject.next({ params }); // executes a call
    }
  }

  public setOffset(offset: number): void {
    this.offset = offset;
  }

  public destroy(): void {
    this._executeSubscription.unsubscribe();
  }

  public get filterAccountingYear(): number {
    return this._filterAccountingYear;
  }

  public set filterAccountingYear(value: number) {
    this._filterAccountingYear = value;
    this._filterAccountingYearSubject.next(value);
  }

  public get toAccountingYear(): number {
    return this._toAccountingYear;
  }

  public set toAccountingYear(value: number) {
    this._toAccountingYear = value;
  }

  public get fromAccountingYear(): number {
    return this._fromAccountingYear;
  }

  public set fromAccountingYear(value: number) {
    this._fromAccountingYear = value;
  }

  public get descAccountingYearsFlag(): boolean {
    return this._descAccountingYearsFlag;
  }

  public set descAccountingYearsFlag(value: boolean) {
    this._descAccountingYearsFlag = value;
  }

  public requiredFeatureKeys(): string[] {
    const allFieldGroups: string[] = [
      ...this.filters.map((f) => f.fieldGroup),
      ...this.orderings.map((f) => f.fieldGroup),
      ...this.selectedColumns.map((f) => f.fieldGroup),
    ];

    const collateralFieldGroups = ['company_collateral', 'company_collateral_aggregate', 'company_collateral_type'];

    let featureKeys: string[] = ['base'];

    if (includesAny(allFieldGroups, collateralFieldGroups)) {
      featureKeys.push('collateral');
    }

    const filteredCompanyFlagTypeUuids: string[] = flatten1(
      this.filters
        .filter((f) => f.fieldKey === 'company_flag_history_aggregate.company_flag_type_uuids')
        .map((f) => f.values),
    );

    featureKeys = [
      ...featureKeys,
      ...filteredCompanyFlagTypeUuids.map((cftUuid) => this._companyFlagTypeUuidToAppFeatureKeyDict[cftUuid]),
    ];

    // TODO: Accounts
    // TODO: Country
    // TODO: And more!

    return arrayUnique(featureKeys);
  }
}
